/**
 * DocumentsSection — documents de paie transmis par le gestionnaire (Batch #14.251).
 * Bulletins PDF · chèques PDF · fichier bancaire CPA-005 · registre — uniquement ce qui a été publié.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, FolderDown, Loader2 } from "lucide-react";
import { API, portalHeaders, SectionCard, YEARS, FREQ_LABELS, fmtDate, portalDownload, err } from "./_shared";

export default function DocumentsSection({ primaryColor }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/paie/documents?year=${year}`, { headers: portalHeaders() });
      setItems(r.data.items || []);
    } catch (e) { err(e, "Erreur chargement des documents"); setItems([]); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  return (
    <SectionCard title="Documents reçus" icon={FolderDown} primaryColor={primaryColor} testId="paie-portal-documents"
      action={(
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border rounded-lg px-3 py-2 text-sm" data-testid="paie-portal-docs-year">
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      )}>
      {items === null ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-300 mx-auto" /></div>
        : items.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8" data-testid="paie-portal-docs-empty">
            Aucun document pour {year}. Vos bulletins, chèques et fichiers bancaires apparaîtront ici lorsque votre gestionnaire vous les transmettra.
          </p>
        ) : (
          <ul className="divide-y" data-testid="paie-portal-docs-list">
            {items.map((r) => (
              <li key={r.id} className="py-4 space-y-2" data-testid={`paie-portal-docs-run-${r.id}`}>
                <div>
                  <div className="font-medium text-gray-900">Paie du {fmtDate(r.pay_date)}</div>
                  <div className="text-xs text-gray-500">Période du {r.period_start} au {r.period_end} · {FREQ_LABELS[r.frequency]} · transmis le {String(r.portal_published_at || "").slice(0, 10)}</div>
                  {r.portal_message && <div className="text-xs text-gray-700 bg-gray-50 border rounded-lg px-3 py-2 mt-2" data-testid={`paie-portal-docs-msg-${r.id}`}>{r.portal_message}</div>}
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {r.docs.map((d) => (
                    <button key={d.kind} type="button" disabled={!!busy}
                      onClick={() => portalDownload(`/portal/paie/runs/${r.id}/doc/${d.kind}`, `${d.kind}_${r.pay_date}.${d.ext}`, (b) => setBusy(b ? `${r.id}-${d.kind}` : ""))}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border bg-white hover:bg-gray-50 text-left transition tap-target"
                      data-testid={`paie-portal-doc-${r.id}-${d.kind}`}>
                      <span className="w-9 h-9 rounded-lg flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                        {busy === `${r.id}-${d.kind}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900 truncate">{d.label}</span>
                        <span className="block text-[11px] text-gray-500 truncate">{d.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
    </SectionCard>
  );
}
